import { FC } from 'react'
import { FormControl, FormLabel } from 'react-bootstrap'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { IProductDimension } from 'shared/models/IProductDimension'

interface DimensionFieldPairProps {
  mmField: keyof Pick<
    IProductDimension,
    'depthInMM' | 'widthInMM' | 'heightInMM'
  >
  percentageField: keyof Pick<
    IProductDimension,
    'depthInPercentage' | 'widthInPercentage' | 'heightInPercentage'
  >
  mmLabel: string
  percentageLabel: string
}

const hasValue = (value: number | string | null | undefined): boolean =>
  value !== null && value !== undefined && value !== '' && !Number.isNaN(value)

const DimensionFieldPair: FC<DimensionFieldPairProps> = ({
  mmField,
  percentageField,
  mmLabel,
  percentageLabel,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IProductDimension>()

  const [mmValue, percentageValue] = useWatch({
    control,
    name: [mmField, percentageField],
  })

  const isMMDisabled = hasValue(percentageValue)
  const isPercentageDisabled = hasValue(mmValue)

  return (
    <div>
      <div className='row mb-4'>
        <FormLabel htmlFor={mmField}>{mmLabel}</FormLabel>
        <Controller
          name={mmField}
          control={control}
          render={({ field }) => (
            <div className='input-group height-40'>
              <FormControl
                type='number'
                id={mmField}
                disabled={isMMDisabled}
                className={errors[mmField] ? 'is-invalid ' : ''}
                value={(field.value as string | number) ?? ''}
                onChange={e => {
                  field.onChange(e.target.value)
                  if (hasValue(e.target.value)) {
                    setValue(percentageField, undefined)
                  }
                }}
              />
              <span className='text-muted input-group-text input-group-text-right'>
                mm
              </span>
              <div
                style={{ height: '1rem' }}
                className='invalid-feedback d-block'
              >
                {errors[mmField]?.message}
              </div>
            </div>
          )}
        />
      </div>
      <div className='row '>
        <FormLabel htmlFor={percentageField}>{percentageLabel}</FormLabel>
        <Controller
          name={percentageField}
          control={control}
          render={({ field }) => (
            <div className='input-group'>
              <span className='text-muted input-group-text input-group-text-left'>
                +
              </span>
              <FormControl
                type='number'
                id={percentageField}
                disabled={isPercentageDisabled}
                className={errors[percentageField] ? 'is-invalid' : ''}
                value={(field.value as string | number) ?? ''}
                onChange={e => {
                  field.onChange(e.target.value)
                  if (hasValue(e.target.value)) {
                    setValue(mmField, undefined)
                  }
                }}
              />
              <span className='text-muted input-group-text input-group-text-right'>
                %
              </span>
              <div className='invalid-feedback d-block'>
                {errors[percentageField]?.message}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  )
}

export default DimensionFieldPair
